import React, { useCallback, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { translate } from '../../../services/translate';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import { useAuth } from '../../../context/Auth';
import { armoryService, CannotOverlapArmoryShiftDate } from '../../../services/central-api/armory';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ArmourerDropdown from '../../../components/armory/ArmourerDropdown';
import MyAppText from '../../../components/MyAppText';
import moment from 'moment';
import DateTimePicker, { CalendarValue } from '../../../components/DateTimePicker';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { Tabs } from '../../../components/Tabs';
import CustomButton from '../../../components/CustomButton';
import DeleteArmoryShiftModal from './DeleteArmoryShiftModal';
import ArmoryShiftFormHistoric from './ArmoryShiftFormHistoric';
import AssistantsArmourerDropdown from '../../../components/armory/AssistantsArmourerDropdown';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';

export default function ArmoryShiftForm({ navigation, route }: {
    navigation: Navigation;
    route: {
        params?: {
            id: number;
        };
    };
}) {
    const { styles } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(0);
    const [userId, setUserId] = useState<number>(0);
    const [startInDateFilter, setStartInDateFilter] = useState<CalendarValue>(null);
    const [startInHourFilter, setStartInHourFilter] = useState<string>('');
    const [finishInDateFilter, setFinishInDateFilter] = useState<CalendarValue>(null);
    const [finishInHourFilter, setFinishInHourFilter] = useState<string>('');
    const [armoryShift, setArmoryShift] = useState<ArmoryShift | null>(null);
    const [assistantArmourersIds, setAssistantArmourersIds] = useState<number[]>([]);

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);
    const [deleteShiftModal, setDeleteFinishModal] = useState(false);

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }

        if (route.params?.id) {
            const response = await armoryService.getArmoryShift(route.params?.id);
            setArmoryShift(response);
            setActingBodyId(response.actingBodyUnit?.actingBody?.id ?? 0);
            setActingBodyUnitId(response.actingBodyUnitId);
            setStartInDateFilter(new Date(response.estimatedStart));
            setStartInHourFilter(moment(response.estimatedStart).format('HH:mm'));
            setFinishInDateFilter(new Date(response.estimatedFinish));
            setFinishInHourFilter(moment(response.estimatedFinish).format('HH:mm'));
            setUserId(response.userId);
            setAssistantArmourersIds(response.AssistantArmourers.map((user) => user.id));
        } else if (
            !authenticatedUser.permissions.armory_manage_all_acting_bodies_armory_shift &&
            authenticatedUser.actingBodyUnit.actingBodyId
        ) {
            setActingBodyId(authenticatedUser.actingBodyUnit.actingBodyId);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            setArmoryShift(null);
            setActingBodyId(0);
            setActingBodyUnitId(0);
            setStartInDateFilter(null);
            setStartInHourFilter('');
            setFinishInDateFilter(null);
            setFinishInHourFilter('');
            setUserId(0);
            setAssistantArmourersIds([]);
            loadPageInfo();
        }
    }, [isFocused, navigation, route.params, authenticatedUser]));

    async function formSubmit() {
        try {
            const start = moment(`${moment(startInDateFilter).format('DD/MM/YYYY')} ${startInHourFilter}`, 'DD/MM/YYYY HH:mm').toDate();
            const finish = moment(`${moment(finishInDateFilter).format('DD/MM/YYYY')} ${finishInHourFilter}`, 'DD/MM/YYYY HH:mm').toDate();
            if (route.params?.id) {
                await armoryService.updateArmoryShift({
                    id: route.params?.id,
                    userId,
                    estimatedFinish: finish,
                    estimatedStart: start,
                    assistantArmourersIds
                });
            } else {
                await armoryService.createArmoryShift({
                    userId,
                    actingBodyUnitId,
                    estimatedFinish: finish,
                    estimatedStart: start,
                    assistantArmourersIds
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            navigation.navigate('ArmoryShifts');
        } catch (err) {
            if (err instanceof CannotOverlapArmoryShiftDate) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message, {
                        estimatedStart: moment(err.estimatedStart).format('DD/MM/YYYY HH:mm'),
                        estimatedFinish: moment(err.estimatedFinish).format('DD/MM/YYYY HH:mm')
                    }),
                    visibilityTime: 10000,
                });
            }
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function isFormValid(): boolean {

        if (!route.params?.id &&
            authenticatedUser?.permissions.armory_manage_all_acting_bodies_armory_shift &&
            (!actingBodyId || actingBodyId == 0)) {
            return false;
        }
        if (!route.params?.id &&
            (authenticatedUser?.permissions.armory_manage_acting_body_units_armory_shift ||
                authenticatedUser?.permissions.armory_manage_all_acting_bodies_armory_shift)
            && (!actingBodyUnitId || actingBodyUnitId == 0)) {
            return false;
        }
        if (startInDateFilter == null || finishInDateFilter == null) {
            return false;
        }
        const start = moment(`${moment(startInDateFilter).format('DD/MM/YYYY')} ${startInHourFilter}`, 'DD/MM/YYYY HH:mm');
        const finish = moment(`${moment(finishInDateFilter).format('DD/MM/YYYY')} ${finishInHourFilter}`, 'DD/MM/YYYY HH:mm');

        return start.isBefore(finish) && moment.duration(finish.diff(start)).asHours() >= 1;
    }

    return <Container>
        <Header
            goBackAction={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('ArmoryShifts')}
            title={route.params?.id && armoryShift?.definitiveFinish ? translate('viewArmourerShift') : route.params?.id ? translate('editArmourerShift') : translate('registerArmourerShift')}
            actions={<>
                {armoryShift && !armoryShift.definitiveFinish && !armoryShift.definitiveStart &&
                    <CustomButton
                        text={translate('delete')}
                        onPress={() => {
                            setDeleteFinishModal(true);
                        }}
                        type='secondary'
                    />
                }
                {!armoryShift?.definitiveFinish &&
                    <CustomButton text={translate('save')} onPress={formSubmit} disabled={!isFormValid()} />
                }
            </>}
        />
        <Card>
            <Tabs tabs={[{
                key: 'details',
                label: translate('details')
            }, {
                key: 'historic',
                label: translate('historic')
            }]}>
                {({ selectedTab }) => {
                    if (selectedTab === 'details') {
                        return <ScrollView contentContainerStyle={{ gap: 24 }}>
                            <View style={{ gap: 16, zIndex: 3 }}>
                                <View style={styles.subTitleContainer}>
                                    <MyAppText style={styles.detailsFont}>{translate('generalData')}</MyAppText>
                                </View>
                                {authenticatedUser?.permissions.armory_manage_all_acting_bodies_armory_shift &&
                                    <ActingBodiesDropdown
                                        includeAllOption={false}
                                        value={actingBodyId}
                                        setValue={setActingBodyId}
                                        zIndex={6}
                                        disabled={!!route.params?.id}
                                        required={!route.params?.id}
                                        labelFontSize={14}
                                    />
                                }
                                {(authenticatedUser?.permissions.armory_manage_all_acting_bodies_armory_shift ||
                                    authenticatedUser?.permissions.armory_manage_acting_body_units_armory_shift) &&
                                    <ActingBodiesUnitsDropdown
                                        value={actingBodyUnitId}
                                        actingBodyId={actingBodyId}
                                        setValue={setActingBodyUnitId}
                                        zIndex={5}
                                        disabled={!!route.params?.id}
                                        required={!route.params?.id}
                                        labelFontSize={14}
                                    />
                                }
                                <ArmourerDropdown
                                    value={userId}
                                    actingBodyId={actingBodyId}
                                    setValue={(value) => {
                                        setUserId(value);
                                        setAssistantArmourersIds([]);
                                    }}
                                    zIndex={4}
                                    required={!armoryShift?.definitiveStart}
                                    labelFontSize={14}
                                    disabled={armoryShift?.definitiveStart != null}
                                />
                                <AssistantsArmourerDropdown
                                    value={assistantArmourersIds}
                                    actingBodyId={actingBodyId}
                                    setValue={setAssistantArmourersIds}
                                    zIndex={3}
                                    required={false}
                                    labelFontSize={14}
                                    userId={userId}
                                    disabled={armoryShift?.definitiveFinish != null}
                                />
                            </View>
                            <View style={{
                                gap: 16,
                                zIndex: estimatedFinishZIndex > estimatedStartZIndex ? estimatedFinishZIndex : estimatedStartZIndex
                            }}>
                                <View style={styles.subTitleContainer}>
                                    <MyAppText style={styles.detailsFont}>{translate('currentShift')}</MyAppText>
                                </View>
                                <View style={{ flexDirection: 'row', gap: 30 }}>
                                    <View style={{ gap: 16 }}>
                                        <View style={styles.datePickerContainer(estimatedStartZIndex)}>
                                            <DateTimePicker
                                                date={startInDateFilter}
                                                onChangeDate={setStartInDateFilter}
                                                time={startInHourFilter}
                                                onChangeTime={setStartInHourFilter}
                                                label={translate('estimatedStart')}
                                                labelFontSize={14}
                                                onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                                                onCalendarClose={() => setEstimatedStartZIndex(2)}
                                                required={armoryShift?.definitiveFinish == null}
                                                disabled={armoryShift?.definitiveFinish != null}
                                            />
                                        </View>
                                        <View style={styles.datePickerContainer(estimatedFinishZIndex)}>
                                            <DateTimePicker
                                                date={finishInDateFilter}
                                                onChangeDate={setFinishInDateFilter}
                                                time={finishInHourFilter}
                                                onChangeTime={setFinishInHourFilter}
                                                label={translate('estimatedFinish')}
                                                labelFontSize={14}
                                                onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                                                onCalendarClose={() => setEstimatedFinishZIndex(1)}
                                                required={armoryShift?.definitiveFinish == null}
                                                disabled={armoryShift?.definitiveFinish != null}
                                            />
                                        </View>
                                    </View>
                                    {armoryShift?.definitiveFinish && armoryShift?.definitiveStart &&
                                        <View style={{ gap: 16 }}>
                                            <View style={styles.datePickerContainer(estimatedStartZIndex)}>
                                                <DateTimePicker
                                                    date={armoryShift.definitiveStart}
                                                    onChangeDate={() => { }}
                                                    time={moment(armoryShift.definitiveStart).format('HH:mm')}
                                                    onChangeTime={() => { }}
                                                    label={translate('definitiveStart')}
                                                    labelFontSize={14}
                                                    disabled={true}
                                                />
                                            </View>
                                            <View style={styles.datePickerContainer(estimatedFinishZIndex)}>
                                                <DateTimePicker
                                                    date={armoryShift.definitiveFinish}
                                                    onChangeDate={() => { }}
                                                    time={moment(armoryShift.definitiveFinish).format('HH:mm')}
                                                    onChangeTime={() => { }}
                                                    label={translate('definitiveFinish')}
                                                    labelFontSize={14}
                                                    disabled={true}
                                                />
                                            </View>
                                        </View>
                                    }
                                </View>
                            </View>
                        </ScrollView>;
                    } else if (selectedTab === 'historic') {
                        return route.params?.id ? <ArmoryShiftFormHistoric armoryShiftId={route.params?.id} /> : <></>;
                    }
                }}
            </Tabs>
        </Card>
        {armoryShift && <DeleteArmoryShiftModal
            isModalVisible={deleteShiftModal}
            setModalVisible={setDeleteFinishModal}
            armoryShiftId={armoryShift.id}
            onClose={() => setDeleteFinishModal(false)}
            onSave={() => navigation.navigate('ArmoryShifts')}
        />}
    </Container>;
}

const styleSheet = createStyleSheet((theme) => ({
    detailsFont: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500']
    },
    subTitleContainer: {
        borderBottomWidth: 1,
        borderColor: theme.colors.primary['200'],
    },
    datePickerContainer: (zIndex: number) => {
        return {
            minWidth: 300,
            maxWidth: 300,
            zIndex: zIndex
        };
    }
}));
